import React, {
  ReactElement,
  Ref,
  forwardRef,
  useContext,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';
import ButtonGroup from '@material-ui/core/ButtonGroup/ButtonGroup';
import Typography from '@material-ui/core/Typography';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import RemoveIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import {
  ActionButtonHandle,
  ApiFilterOperation,
  DetailContext,
  DetailMode,
  FieldFilter,
  RemoteTableFieldContext,
  TableFieldToolbarButton,
  TableFieldToolbarButtons,
  TableFieldToolbarProps,
  UserContext,
} from '@eas/common-web';
import { DetailActionButton } from '@components/action-button/detail-action-button';
import { useStyles } from '@components/form/table-field/form-table-toolbar-styles';
import { TextNote } from '@models';
import { EsFileState, Role } from '@enums';
import { useAddNoteDialog } from './dialog-actions/add-note-hook';
import { useDeleteNoteDialog } from './dialog-actions/delete-note-hook';
import { useEditNoteDialog } from './dialog-actions/edit-note-hook';
import { NoteDialog } from './note-dialog';
import { useValidationSchema } from './note-schema';

export const NoteToolbar = forwardRef(function NotesToolbar(
  {
    selectedIndex,
    setSelectedIndex,
    disabled,
  }: TableFieldToolbarProps & { disabled: boolean },
  ref: Ref<ActionButtonHandle>
) {
  const classes = useStyles();

  const intl = useIntl();

  const { hasPermission, user } = useContext(UserContext);
  const { source: detailSource } = useContext(DetailContext);
  const { source: tableSource } = useContext(RemoteTableFieldContext);
  const notes = tableSource.items;

  const validationSchema = useValidationSchema();

  const { callApi: addNoteApiCall } = useAddNoteDialog();
  const { callApi: editNoteApiCall } = useEditNoteDialog();
  const { callApi: deleteNoteApiCall } = useDeleteNoteDialog();

  /** Allow Lustrum to edit notes on files in the NEW state. */
  const isLustrumOnNewFile =
    hasPermission(Role.LUSTRUM) &&
    (detailSource?.data as { state?: EsFileState } | undefined)?.state ===
      EsFileState.NEW;

  const hasPermissionToEdit =
    hasPermission(Role.ADMIN) ||
    hasPermission(Role.INDEX) ||
    isLustrumOnNewFile ||
    (selectedIndex &&
      tableSource.items?.[selectedIndex]?.createdBy?.id === user.id);

  const selectedNote = (
    selectedIndex !== undefined ? notes?.[selectedIndex] : {}
  ) as TextNote;

  const isSelected = selectedIndex !== undefined;

  useEffect(() => {
    setSelectedIndex(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailSource]);

  return (
    <div className={classes.tableActions}>
      <ButtonGroup className={classes.buttonGroup}>
        <DetailActionButton
          promptKey="ADD_NOTE"
          apiCall={addNoteApiCall}
          buttonLabel={intl.formatMessage({
            id: 'ES__NOTES_TOOLBAR__ADD_NOTE',
            defaultMessage: 'Přidat novou poznámku',
          })}
          dialogTitle={intl.formatMessage({
            id: 'ES__NOTES_TOOLBAR__ADD_NOTE__TITLE',
            defaultMessage: 'Poznámka',
          })}
          dialogText={intl.formatMessage({
            id: 'ES__NOTES_TOOLBAR__ADD_NOTE__TEXT',
            defaultMessage: 'Vyplňte text poznámky.',
          })}
          dialogWidth={700}
          ButtonComponent={({ onClick, label }) => (
            <TableFieldToolbarButton
              IconComponent={ControlPointIcon}
              show={true}
              disabled={disabled}
              title={label}
              onClick={onClick}
              color="primary"
              variant="contained"
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'ES__NOTES_TOOLBAR__ADD_NOTE__BUTTON',
                  defaultMessage: 'Přidat',
                })}
              </Typography>
            </TableFieldToolbarButton>
          )}
          FormFields={NoteDialog}
          modes={[DetailMode.VIEW, DetailMode.EDIT]}
          formValidationSchema={validationSchema}
        />
        <DetailActionButton
          promptKey="EDIT_NOTE"
          apiCall={(id, formData) =>
            editNoteApiCall(tableSource.items![selectedIndex!].id, formData)
          }
          ref={ref}
          buttonLabel={intl.formatMessage({
            id: 'ES__NOTES_TOOLBAR__EDIT_NOTE',
            defaultMessage: 'Editovat poznámku',
          })}
          dialogTitle={intl.formatMessage({
            id: 'ES__NOTES_TOOLBAR__EDIT_NOTE__TITLE',
            defaultMessage: 'Poznámka',
          })}
          dialogText={intl.formatMessage({
            id: 'ES__NOTES_TOOLBAR__EDIT_NOTE__TEXT',
            defaultMessage: 'Vyplňte nový text poznámky.',
          })}
          dialogWidth={700}
          FormFields={NoteDialog}
          ButtonComponent={({ onClick, label }) => (
            <TableFieldToolbarButton
              IconComponent={EditIcon}
              show={true}
              disabled={disabled || !isSelected || !hasPermissionToEdit}
              title={label}
              onClick={onClick}
              color="default"
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'ES__NOTES_TOOLBAR__EDIT_NOTE__BUTTON',
                  defaultMessage: 'Upravit',
                })}
              </Typography>
            </TableFieldToolbarButton>
          )}
          formInitialValues={selectedNote}
          modes={[DetailMode.VIEW, DetailMode.EDIT]}
          formValidationSchema={validationSchema}
        />
        <DetailActionButton
          promptKey="DELETE_NOTE"
          apiCall={() => deleteNoteApiCall(notes![selectedIndex!].id)}
          buttonLabel={intl.formatMessage({
            id: 'ES__NOTES_TOOLBAR__DELETE_NOTE',
            defaultMessage: 'Odstranit poznámku',
          })}
          dialogTitle={intl.formatMessage({
            id: 'ES__NOTES_TOOLBAR__DELETE_NOTE__TITLE',
            defaultMessage: 'Poznámka',
          })}
          dialogText={intl.formatMessage({
            id: 'ES__NOTES_TOOLBAR__DELETE_NOTE__TEXT',
            defaultMessage: 'Skutečně chcete odstranit poznámku?',
          })}
          ButtonComponent={({ onClick, label }) => (
            <TableFieldToolbarButton
              IconComponent={RemoveIcon}
              show={true}
              disabled={disabled || !isSelected || !hasPermissionToEdit}
              title={label}
              onClick={onClick}
              color="secondary"
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'ES__NOTES_TOOLBAR__DELETE_NOTE__BUTTON',
                  defaultMessage: 'Smazat',
                })}
              </Typography>
            </TableFieldToolbarButton>
          )}
          modes={[DetailMode.VIEW, DetailMode.EDIT]}
        />
      </ButtonGroup>
      <ButtonGroup className={classes.buttonGroup}>
        <TableFieldToolbarButtons.FilterButton
          color="default"
          variant="text"
          source={tableSource}
          filtersFields={[
            {
              label: intl.formatMessage({
                id: 'ES__NOTES_TOOLBAR__FILTER__TEXT',
                defaultMessage: 'Text',
              }),
              datakey: 'text',
              filterkey: 'text',
              defaultOperation: ApiFilterOperation.CONTAINS,
              FilterComponent: FieldFilter.FilterTextCell,
            },
          ]}
        />
      </ButtonGroup>
    </div>
  );
}) as (
  p: TableFieldToolbarProps & {
    disabled: boolean;
    ref: Ref<ActionButtonHandle>;
  }
) => ReactElement;
